/**
 * Web Push opt-in / opt-out helpers.
 *
 * Pure-JS helpers — no React — so they can also run from a one-shot
 * Settings handler or be tested directly in DevTools. Every function
 * resolves to `{ ok: boolean, status, ... }` so callers can render a
 * single error toast without parsing exception types.
 */
import { api } from "@/lib/api";

/** True if the browser exposes the Push API + Notifications + service workers. */
export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Convert a URL-safe-base64 string (VAPID public key) into the Uint8Array
 *  the `PushManager.subscribe` API expects as applicationServerKey. */
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function getRegistration() {
  // Use the existing registration; sw.js is registered in index.js on load.
  // Some environments leave `navigator.serviceWorker.ready` slow to
  // resolve right after a deploy (the new SW is still installing) — race
  // it with a generous timeout AND, if no registration exists yet, kick
  // off a fresh registration ourselves before the race so the SW has a
  // chance to install during the wait instead of timing out.
  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) {
    try {
      await navigator.serviceWorker.register("/sw.js");
    } catch (_) { /* swallow — race below will surface the real error */ }
  }
  const ready = navigator.serviceWorker.ready;
  const timeout = new Promise((_resolve, reject) =>
    setTimeout(() => reject(new Error("sw-not-ready")), 10000),
  );
  return Promise.race([ready, timeout]);
}

/** Returns the current local subscription, or null. Resolves to null on
 *  any error (timeout, SW not registered, etc.) so callers can render a
 *  sensible default UI without unhandled rejections. */
export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await getRegistration();
    return reg.pushManager.getSubscription();
  } catch (_) {
    return null;
  }
}

/** Idempotent — calling twice in a row is a no-op (returns the existing sub).
 *  Always resolves with `{ok, error?, detail?}`; never throws. The caller
 *  is expected to display a friendly toast keyed off `error`. */
export async function subscribePush() {
  if (!isPushSupported()) {
    return { ok: false, error: "unsupported" };
  }
  // Notifications API permission gate. Safari / iOS only resolve `granted`
  // once the PWA is installed to home screen — surface a friendly message
  // up-stream rather than silently failing.
  let perm = Notification.permission;
  if (perm === "default") {
    try {
      perm = await Notification.requestPermission();
    } catch (e) {
      return { ok: false, error: "permission_error", detail: String(e) };
    }
  }
  if (perm !== "granted") {
    return { ok: false, error: perm === "denied" ? "denied" : "dismissed" };
  }

  let reg;
  try {
    reg = await getRegistration();
  } catch (e) {
    // The SW hasn't finished activating yet. Most often happens right
    // after a deploy when the new SW is still installing — ask the user
    // to retry in a moment rather than throwing.
    return { ok: false, error: "sw_not_ready", detail: String(e) };
  }

  let sub;
  try {
    sub = await reg.pushManager.getSubscription();
  } catch (e) {
    return { ok: false, error: "getSubscription_failed", detail: String(e) };
  }

  if (!sub) {
    let vapid_public_key;
    try {
      ({ vapid_public_key } = await api.get("/push/vapid-key").then((r) => r.data));
    } catch (e) {
      return { ok: false, error: "vapid_fetch_failed", detail: String(e?.response?.data?.detail || e?.message || e) };
    }
    if (!vapid_public_key) {
      return { ok: false, error: "vapid_missing" };
    }
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid_public_key),
      });
    } catch (e) {
      // Common causes: stale GCM sender, applicationServerKey mismatch,
      // browser blocked PushService. Log to console so devtools can show
      // the precise reason; surface a coded error to the UI.
      console.warn("[push] pushManager.subscribe failed", e);
      // If a stale subscription exists with a DIFFERENT applicationServerKey
      // (e.g., VAPID keys were rotated), Chrome throws InvalidStateError —
      // unsubscribe the stale one and retry once.
      if (String(e?.name) === "InvalidStateError") {
        try {
          const stale = await reg.pushManager.getSubscription();
          if (stale) await stale.unsubscribe();
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapid_public_key),
          });
        } catch (retryErr) {
          return { ok: false, error: "subscribe_failed", detail: String(retryErr?.message || retryErr) };
        }
      } else {
        return { ok: false, error: "subscribe_failed", detail: String(e?.message || e) };
      }
    }
  }

  // POST to backend (idempotent server-side upsert).
  try {
    const json = sub.toJSON();
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
      expirationTime: json.expirationTime || null,
    });
  } catch (e) {
    return { ok: false, error: "backend_subscribe_failed", detail: String(e?.response?.data?.detail || e?.message || e) };
  }
  return { ok: true, subscription: sub };
}

export async function unsubscribePush() {
  if (!isPushSupported()) return { ok: true };
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const { endpoint } = sub.toJSON();
  try {
    await sub.unsubscribe();
  } catch (_) { /* ignore */ }
  try {
    await api.delete("/push/unsubscribe", { data: { endpoint } });
  } catch (_) { /* ignore */ }
  return { ok: true };
}

export async function sendTestPush() {
  return api.post("/push/test").then((r) => r.data);
}

export async function getPushStatus() {
  return api.get("/push/status").then((r) => r.data);
}
